import { CommissionTypeAPI } from '@business/api/commission_type';
import { CommissionTypeId, CommissionTypeRequest, CommissionTypeResponse } from '@business/entities/commission';
import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { R } from '@core/utils/r';
import { Repository } from '@core/utils/repository';
import { showDeleteConfirmation } from '@modules/common';
import { map, switchMap } from 'rxjs/operators';

/** Commission types (Salesman Commission, Staff Gift...) with their payroll codes. Managed in Settings by admins. */
export class CommissionTypeBloc extends BaseBloc {
  private readonly repo = new Repository<CommissionTypeResponse>({
    getItemId: item => item.id,
  });

  readonly items$ = this.repo.items$.pipe(map(items => R.sortBy(items, item => item.title.toLowerCase())));
  readonly selectItem = this.repo.selectItem;

  onReset() {
    this.repo.reset();
  }

  fetchCommissionTypes = () => {
    return CommissionTypeAPI.fetchCommissionTypes().pipe(
      this.repo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_commission_types_error') }),
    );
  };

  createCommissionType = (data: CommissionTypeRequest) => {
    return CommissionTypeAPI.createCommissionType(data).pipe(
      this.repo.ops.reset(),
      handleMessage({ type: requestMessage('create_commission_type') }),
    );
  };

  updateCommissionType = (id: CommissionTypeId, data: CommissionTypeRequest) => {
    return CommissionTypeAPI.updateCommissionType(id, data).pipe(
      this.repo.ops.reset(),
      handleMessage({ type: requestMessage('update_commission_type') }),
    );
  };

  deleteCommissionType = (id: CommissionTypeId) => {
    return showDeleteConfirmation(
      'Delete Commission Type',
      'Do you really want to remove this commission type? Commissions that already use it keep it.',
    ).pipe(
      switchMap(() => CommissionTypeAPI.deleteCommissionType(id)),
      this.repo.ops.removeOne(() => id),
      handleMessage({ type: requestMessage('delete_commission_type') }),
    );
  };
}
